/**
 * Profile.js — Fonctions utilitaires pour le composant Profile
 *
 * Exportées et utilisées dans Profile.jsx :
 *  - validateProfileForm  : vérifie les champs du formulaire d'identité
 *  - getPasswordStrength  : calcule la force d'un mot de passe (score 0–4)
 *  - validatePasswordForm : vérifie les règles du changement de mot de passe
 *  - formatDate           : formate une date en français (ex: "05 janvier 2024")
 *  - getInitials          : génère les initiales de l'utilisateur (ex: "AB")
 *  - generateAvatarColor  : choisit une couleur d'avatar déterministe selon le nom
 */

/** Valide les champs du formulaire de profil (identité + email + téléphone) */
export function validateProfileForm(data) {
	const errors = {};
	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	// Supprime les caractères non numériques pour compter les chiffres réels
	const phoneDigits = String(data.tel || '').replace(/\D/g, '');

	if (!String(data.prenom || '').trim()) errors.prenom = 'Le prenom est obligatoire.';
	if (!String(data.nom || '').trim()) errors.nom = 'Le nom est obligatoire.';
	if (!String(data.email || '').trim()) errors.email = "L'email est obligatoire.";
	else if (!emailPattern.test(String(data.email).trim())) errors.email = "Format d'email invalide.";
	if (!String(data.tel || '').trim()) errors.tel = 'Le telephone est obligatoire.';
	else if (phoneDigits.length < 10) errors.tel = 'Le telephone doit contenir au moins 10 chiffres.';
	if (!String(data.sexe || '').trim()) errors.sexe = 'Le sexe est obligatoire.';

	return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * getPasswordStrength — Calcule la force d'un mot de passe sur 4 critères :
 *  1. Au moins 8 caractères
 *  2. Au moins une majuscule
 *  3. Au moins un chiffre
 *  4. Au moins un caractère spécial
 * Retourne { score (0–4), label, color } pour l'affichage de la barre de force.
 */
export function getPasswordStrength(password) {
	const value = String(password || '');
	let score = 0;

	if (value.length >= 8) score += 1;
	if (/[A-Z]/.test(value)) score += 1;
	if (/[0-9]/.test(value)) score += 1;
	if (/[^A-Za-z0-9]/.test(value)) score += 1;

	const levels = [
		{ label: 'Faible', color: '#ffb5b5' },
		{ label: 'Moyen', color: '#e8c547' },
		{ label: 'Fort', color: '#8adfff' },
		{ label: 'Tres fort', color: '#7ad3ab' }
	];

	if (score === 0) return { score: 0, label: 'Faible', color: '#ffb5b5' };
	const level = levels[Math.max(0, Math.min(score - 1, levels.length - 1))];
	return { score, label: level.label, color: level.color };
}

/** Valide le formulaire de changement de mot de passe (ancien + nouveau + confirmation) */
export function validatePasswordForm(current, newPwd, confirm) {
	const errors = {};
	const currentValue = String(current || '');
	const newValue = String(newPwd || '');
	const confirmValue = String(confirm || '');

	if (!currentValue) errors.currentPassword = 'Le mot de passe actuel est obligatoire.';
	if (!newValue) errors.newPassword = 'Le nouveau mot de passe est obligatoire.';
	else {
		if (newValue.length < 8) errors.newPassword = 'Minimum 8 caracteres requis.';
		if (!/[A-Z]/.test(newValue)) errors.newPassword = 'Le mot de passe doit contenir une majuscule.';
		if (!/[0-9]/.test(newValue)) errors.newPassword = 'Le mot de passe doit contenir un chiffre.';
		if (!/[^A-Za-z0-9]/.test(newValue)) errors.newPassword = 'Le mot de passe doit contenir un caractere special.';
	}
	if (!confirmValue) errors.confirmPassword = 'Confirmez le nouveau mot de passe.';
	else if (newValue !== confirmValue) errors.confirmPassword = 'Les nouveaux mots de passe ne correspondent pas.';

	return { valid: Object.keys(errors).length === 0, errors };
}

/** Formate une date ISO en texte lisible (ex: "05 janvier 2024") */
export function formatDate(dateStr) {
	if (!dateStr) return '';
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) return '';
	return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Retourne les initiales de l'utilisateur (ex: "Ali Baba" → "AB") */
export function getInitials(prenom, nom) {
	return `${String(prenom || '').charAt(0)}${String(nom || '').charAt(0)}`.toUpperCase() || 'UX';
}

/**
 * generateAvatarColor — Choisit une couleur dans la palette selon un hash du nom.
 * Déterministe : le même nom donne toujours la même couleur, sans aléatoire.
 */
export function generateAvatarColor(name) {
	const palette = ['#16c9ff', '#2a6dff', '#1199ff', '#7ad3ab', '#8adfff'];
	const value = String(name || 'UltimateX');
	let hash = 0;
	for (let i = 0; i < value.length; i += 1) {
		hash = value.charCodeAt(i) + ((hash << 5) - hash);
	}
	return palette[Math.abs(hash) % palette.length];
}
